import * as React from 'react';
import { faMagnifyingGlass } from '@fortawesome/pro-regular-svg-icons/faMagnifyingGlass';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { TableColumnType } from 'antd';
import { Button, Input, Space } from 'antd';
import type { FilterValue } from 'antd/es/table/interface';
import { t } from 'i18next';
import { generateDaysForLocale } from 'pos-shared/utils/dateTimeHelpers.ts';

import { convertISOStringToLocalDate } from './dateHelpers.ts';

export const extractSingleFilterSelection = (
  filters: Record<string, FilterValue | null>,
  key: string
) => {
  if (!filters || !filters[key] || !filters[key]?.length) return undefined;

  // @ts-ignore - typescript claiming filters[key] can be null!
  return filters[key][0] as string;
};

// fe filtering
export const createTableFilters = <
  T extends { [key: string]: string | number },
>(
  data: T[],
  field: string,
  dataIsDates = false,
  dataIsNumeric = false
) => {
  const uniqueValues = new Set<number | string>();

  data.forEach((dataItem: T) => {
    uniqueValues.add(dataItem[field]);
  });

  let sortedValues = Array.from(uniqueValues);

  if (dataIsNumeric) {
    sortedValues = sortedValues.sort((a, b) => (a as number) - (b as number));
  }

  return sortedValues.map((value) => ({
    text: dataIsDates ? convertISOStringToLocalDate(value as string) : value,
    value: value,
  }));
};

export const filterByProperty = <
  T extends Record<string, string | number | React.Key>,
>(
  value: React.Key | boolean,
  record: T,
  propertyKey: keyof T
) => {
  const propertyValue = record[propertyKey];
  return (
    propertyValue &&
    String(propertyValue).toLowerCase().indexOf(String(value).toLowerCase()) ===
      0
  );
};

// fe sorting
export const sortTimes = (timeStringA: string, timeStringB: string) => {
  const timeA = new Date(`1970-01-01T${timeStringA}`).getTime();
  const timeB = new Date(`1970-01-01T${timeStringB}`).getTime();
  return timeA - timeB;
};

export const sortDates = <
  T extends Partial<Record<keyof T, string | number | Date | null>>,
>(
  a: T,
  b: T,
  property: keyof T
) => {
  const dateA = a[property]
    ? new Date(a[property] as string | number | Date)
    : new Date(0); // Treat null as earliest date
  const dateB = b[property]
    ? new Date(b[property] as string | number | Date)
    : new Date(0);

  return dateA.getTime() - dateB.getTime();
};

export const sortStrings = <
  T extends Record<string, string | number | undefined | null>,
>(
  a: T,
  b: T,
  property: keyof T
): number => {
  const valueA = a[property] == null ? '' : String(a[property]); // Treat null or undefined as empty string
  const valueB = b[property] == null ? '' : String(b[property]);

  return valueA.localeCompare(valueB);
};

export const sortWeekdays = <
  T extends Record<string | number, string | number>,
>(
  a: T,
  b: T,
  property: keyof T
): number => {
  if (!a || !b) return 0;
  const weekdays = generateDaysForLocale(); // TODO take into consideration if we want to pass the locale through here as well

  return (
    weekdays.indexOf(String(a[property])) -
    weekdays.indexOf(String(b[property]))
  );
};

export const searchFilterByProperty = <
  T extends Record<string, string | number>,
>(
  dataIndex: keyof T,
  placeholder = 'Search'
): Pick<TableColumnType<T>, 'filterDropdown' | 'filterIcon' | 'onFilter'> => {
  return {
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
    }) => (
      <Space
        style={{ padding: 8 }}
        direction="vertical"
        onKeyDown={(e) => e.stopPropagation()}
      >
        <Input
          placeholder={placeholder}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() => confirm()}
        />
        <Space style={{ display: 'flex', justifyContent: 'space-between' }}>
          <Button
            type="link"
            size="small"
            onClick={() => {
              clearFilters?.();
              confirm();
            }}
          >
            {t('common.buttons.clear')}
          </Button>

          <Button type="primary" size="small" onClick={() => confirm()}>
            {t('common.buttons.search')}
          </Button>
        </Space>
      </Space>
    ),
    filterIcon: (filtered: boolean) => (
      <FontAwesomeIcon
        icon={faMagnifyingGlass}
        color={filtered ? '#1677ff' : undefined}
      />
    ),
    onFilter: (value, record) => filterByProperty(value, record, dataIndex),
  };
};
